import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { Alert, Button, Stack, type AlertColor } from '@mui/material';
import { useZeroXWithdrawal, type ZeroXWithdrawalPhase } from '../../../hooks/payment/useZeroXWithdrawal';
import useLocalization from '../../../hooks/useLocalization';
import { WITHDRAWAL_STATE_LABEL_KEYS, WITHDRAWAL_STATE_MESSAGE_KEYS } from '../../../utils/transactionPresentation';
import { zeroXCashierStyles as styles, zeroXCashierFieldStyles } from '../../../styles/components/zeroXCashier';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import { mergeSx } from '../../../utils/styles';
import ZeroXActionButton from './ZeroXActionButton';
import ZeroXWithdrawalSummary from './ZeroXWithdrawalSummary';
import ZeroXWithdrawalEditing from './ZeroXWithdrawalEditing';

type WithdrawalController = ReturnType<typeof useZeroXWithdrawal>;

type WithdrawalStageProps = {
  phase: ZeroXWithdrawalPhase['name'];
  label: string;
  children: ReactNode;
};

const WithdrawalStage = ({ phase, label, children }: WithdrawalStageProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (phase !== 'confirming') {
      ref.current?.focus();
    }
  }, [phase]);

  return (
    <Stack ref={ref} role='region' tabIndex={-1} aria-label={label} sx={styles.stage}>
      {children}
    </Stack>
  );
};

const getReviewActionKey = (phase: ZeroXWithdrawalPhase['name']) => {
  switch (phase) {
    case 'expired':
      return 'zeroXRefreshQuote';
    case 'uncertain':
      return 'zeroXRetrySameRequest';
    case 'confirming':
      return 'zeroXConfirmingWithdrawal';
    default:
      return 'zeroXConfirmWithdrawal';
  }
};

const WithdrawalResult = ({ controller }: { controller: WithdrawalController }) => {
  const { t } = useLocalization();
  const { formInset } = useSkinStyles(zeroXCashierFieldStyles);
  const { phase, reset, viewHistory } = controller;

  if (phase.name !== 'done') {
    return null;
  }

  const { result, quote } = phase;
  const title = t(WITHDRAWAL_STATE_LABEL_KEYS[result.status]);
  let severity: AlertColor = 'info';

  if (result.status === 'confirmed') {
    severity = 'success';
  }

  if (result.status === 'aborted' || result.status === 'rejected') {
    severity = 'error';
  }

  return (
    <Stack sx={mergeSx(styles.root, formInset)}>
      <WithdrawalStage phase={phase.name} label={title}>
        <Alert severity={severity}>{t(WITHDRAWAL_STATE_MESSAGE_KEYS[result.status])}</Alert>
        <ZeroXWithdrawalSummary quote={{ ...quote, ...result }} />
      </WithdrawalStage>

      <Stack sx={styles.actions}>
        <ZeroXActionButton onClick={viewHistory} label={t('zeroXViewHistory')} />
        <Button variant='secondary' type='button' fullWidth onClick={reset}>
          {t('zeroXNewWithdrawal')}
        </Button>
      </Stack>
    </Stack>
  );
};

const WithdrawalReview = ({ controller }: { controller: WithdrawalController }) => {
  const { t } = useLocalization();
  const { formInset } = useSkinStyles(zeroXCashierFieldStyles);
  const { phase, quote, message, edit, requestPreview, confirm } = controller;
  const busy = phase.name === 'confirming';
  const canEdit = phase.name === 'reviewing' || phase.name === 'expired';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (phase.name === 'expired') {
      void requestPreview();
      return;
    }

    void confirm();
  };

  if (!quote) {
    return null;
  }

  return (
    <Stack component='form' onSubmit={handleSubmit} sx={mergeSx(styles.root, formInset)} aria-busy={busy}>
      <WithdrawalStage phase={phase.name} label={t('zeroXReviewWithdrawal')}>
        {message && <Alert severity={message.severity}>{t(message.key, message.values)}</Alert>}
        <ZeroXWithdrawalSummary quote={quote} />
      </WithdrawalStage>

      <Stack sx={styles.actions}>
        <ZeroXActionButton type='submit' loading={busy} label={t(getReviewActionKey(phase.name))} />
        {canEdit && (
          <Button variant='secondary' type='button' fullWidth onClick={edit}>
            {t('zeroXEditDetails')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const ZeroXWithdrawForm = () => {
  const controller = useZeroXWithdrawal();

  switch (controller.phase.name) {
    case 'editing':
    case 'previewing':
      return <ZeroXWithdrawalEditing controller={controller} />;

    case 'done':
      return <WithdrawalResult controller={controller} />;

    default:
      return <WithdrawalReview controller={controller} />;
  }
};

export default ZeroXWithdrawForm;
